import React, { useCallback } from "react";

import {
  faBan,
  faBracketsCurly,
  faBug,
  faClone,
} from "@fortawesome/pro-regular-svg-icons";

import {
  useAgentSessionState,
  useCancelSession,
} from "src/api/agents/execution";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdownElement } from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { SessionResponse, SessionStatus } from "src/clients/agent-management";
import { toastActions } from "src/design-system/Toast/utils";
import { usePowertools } from "src/powertools/hooks";
import { useWsInfo } from "src/powertools/useWsInfo";
import { getAgentLogsInsightsLink } from "src/powertools/utils";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { copyTextToClipboard } from "src/utils/clipboard";

type AgentSessionActionsMenuProps = {
  sessionId: string | null;
  backendStatus: SessionStatus | undefined;
  rawSessionData: SessionResponse | null;
  dataLoc?: string;
};

export const AgentSessionActionsMenu: React.FC<
  AgentSessionActionsMenuProps
> = ({ sessionId, backendStatus, rawSessionData, dataLoc }) => {
  const { workspace } = useWorkspaceContext();
  const wsInfo = useWsInfo(workspace);
  const isPowerToolsEnabled = usePowertools();

  const sessionState = useAgentSessionState(sessionId, backendStatus);
  const cancelSession = useCancelSession();

  const handleCancel = useCallback(() => {
    if (!sessionId) return;
    cancelSession(sessionId);
  }, [sessionId, cancelSession]);

  const handleCopySessionId = useCallback(async () => {
    if (!sessionId) return;
    try {
      await copyTextToClipboard(sessionId);
      toastActions.success({ title: "Copied to clipboard!" });
    } catch {
      toastActions.failure({
        title: "Failed to copy to clipboard",
        description: "Check your browser's clipboard settings",
      });
    }
  }, [sessionId]);

  const handleCopyRawJson = useCallback(async () => {
    if (!rawSessionData) return;
    try {
      await copyTextToClipboard(JSON.stringify(rawSessionData, null, 2));
      toastActions.success({ title: "Copied to clipboard!" });
    } catch {
      toastActions.failure({
        title: "Failed to copy to clipboard",
        description: "Check your browser's clipboard settings",
      });
    }
  }, [rawSessionData]);

  const handleDebugLogs = useCallback(() => {
    if (wsInfo.data?.stack_name && wsInfo.data?.region && sessionId) {
      window.open(
        getAgentLogsInsightsLink(
          wsInfo.data.stack_name,
          wsInfo.data.region,
          sessionId,
        ),
        "_blank",
      );
    }
  }, [wsInfo.data?.stack_name, wsInfo.data?.region, sessionId]);

  const showCancel =
    !!sessionId &&
    sessionState?.status === "active" &&
    sessionState.substatus != "cancelling";
  const showCopyId = !!sessionId;
  const showCopyJson = isPowerToolsEnabled && !!rawSessionData;
  const showDebugLogs = isPowerToolsEnabled && !!wsInfo.data;

  const candidates: { include: boolean; element: OptionsDropdownElement }[] = [
    {
      include: showCancel,
      element: { key: "Cancel run", icon: faBan, action: handleCancel },
    },
    {
      include: showCopyId,
      element: {
        key: "Copy run ID",
        icon: faClone,
        action: handleCopySessionId,
      },
    },
    {
      include: showCopyJson,
      element: {
        key: "Copy raw trace JSON",
        icon: faBracketsCurly,
        action: handleCopyRawJson,
      },
    },
    {
      include: showDebugLogs,
      element: { key: "Debug logs", icon: faBug, action: handleDebugLogs },
    },
  ];

  const elements: OptionsDropdownElement[] = candidates
    .filter((c) => c.include)
    .map((c) => c.element);

  if (elements.length === 0) {
    return null;
  }

  return (
    <EllipsisOptionsDropdown
      dataLoc={dataLoc ?? "agent-session-actions-menu"}
      elements={elements}
    />
  );
};
